import React, { Component, ErrorInfo } from 'react';
import {
  trackException,
  SeverityLevel,
  markErrorAsProcessed,
  getLastRoute,
  getBreadcrumbs,
  getLastFailedApi,
} from '../../util/appInsights';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('App crashed:', error, info.componentStack);
    }
    
    // Marcar erro para evitar duplicação em window.onerror
    markErrorAsProcessed(error);

    // Reportar para Application Insights com SeverityLevel Critical e propriedades enriquecidas
    const lastFailedApi = getLastFailedApi();
    trackException(error, SeverityLevel.Critical, {
      errorType: 'react_render_crash',
      isFatal: true,
      componentStack: info.componentStack,
      lastRoute: getLastRoute(),
      breadcrumbs: getBreadcrumbs(),
      lastFailedApi: lastFailedApi
        ? {
            url: lastFailedApi.url,
            status: lastFailedApi.status,
            timestamp: lastFailedApi.timestamp,
          }
        : null,
    });
  }

  handleReload = () => {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="error-boundary-fallback"
          role="alert"
          style={{
            padding: '2.5rem 1rem',
            textAlign: 'center',
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              width: '100%',
              margin: '0 auto',
              background: '#ffffff',
              borderRadius: '8px',
              padding: '2rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <h2
              style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2d3748',
                marginBottom: '0.75rem',
              }}
            >
              Ocorreu um erro inesperado
            </h2>
            <p
              style={{
                fontSize: '0.95rem',
                color: '#4a5568',
                lineHeight: 1.5,
                marginBottom: '1.5rem',
              }}
            >
              Pedimos desculpa pelo incómodo. Ocorreu uma falha na apresentação deste conteúdo.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                display: 'inline-block',
                padding: '0.625rem 1.5rem',
                backgroundColor: '#0072ce',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.9rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Recarregar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
